
import { isSSR } from '../isSSR';

const POSITION_CACHE_KEY = 'gildy_geo_position';
const POSITION_CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes

// Cache the position in localStorage
export const cachePosition = (position: GeolocationPosition): void => {
  if (isSSR()) return;
  
  try {
    const cachedData = {
      coords: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed
      },
      timestamp: position.timestamp || Date.now()
    };
    
    localStorage.setItem(POSITION_CACHE_KEY, JSON.stringify(cachedData));
    console.log(`Position cached: ${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`);
  } catch (error) {
    console.error("Error caching position:", error);
  }
};

// Get the cached position from localStorage
export const getCachedPosition = (): GeolocationPosition | null => {
  if (isSSR()) return null;
  
  try {
    const cachedDataString = localStorage.getItem(POSITION_CACHE_KEY);
    if (!cachedDataString) return null;
    
    const cachedData = JSON.parse(cachedDataString);
    
    // Validate cached data has required properties
    if (!cachedData || !cachedData.coords || 
        typeof cachedData.coords.latitude !== 'number' || 
        typeof cachedData.coords.longitude !== 'number') {
      console.log("Invalid cached position data, clearing cache");
      localStorage.removeItem(POSITION_CACHE_KEY);
      return null;
    }
    
    // Check if cache is expired
    const now = Date.now();
    const cacheAge = now - (cachedData.timestamp || 0);
    
    if (cacheAge > POSITION_CACHE_EXPIRY) {
      console.log("Cached position expired, clearing cache");
      localStorage.removeItem(POSITION_CACHE_KEY);
      return null;
    }
    
    console.log(`Using cached position from: ${new Date(cachedData.timestamp).toLocaleTimeString()}`);
    return cachedData as GeolocationPosition;
  } catch (error) {
    console.error("Error retrieving cached position:", error);
    // Clear corrupted cache
    try {
      localStorage.removeItem(POSITION_CACHE_KEY);
    } catch (e) {
      // Ignore errors during cleanup
    }
    return null;
  }
};

// Get age of cached position in minutes
export const getCachedPositionAge = (): number | null => {
  if (isSSR()) return null;
  
  try {
    const cachedDataString = localStorage.getItem(POSITION_CACHE_KEY);
    if (!cachedDataString) return null;
    
    const cachedData = JSON.parse(cachedDataString);
    
    if (!cachedData || !cachedData.timestamp) return null;
    
    const now = Date.now();
    const cacheAgeInMs = now - cachedData.timestamp;
    return Math.floor(cacheAgeInMs / (1000 * 60)); // Convert to minutes
  } catch (error) {
    console.error("Error calculating cached position age:", error);
    return null;
  }
};

// Clear the position cache
export const clearPositionCache = (): void => {
  if (isSSR()) return;
  
  try {
    localStorage.removeItem(POSITION_CACHE_KEY);
    console.log("Position cache cleared");
  } catch (error) {
    console.error("Error clearing position cache:", error);
  }
};
